import { useState, useEffect, useCallback } from 'react';
import { analyticsAPI, providerAPI } from '../../services/api';
import EstimateAccuracy from './EstimateAccuracy';

const AnalyticsDashboard = () => {
  // Date range - default last 30 days
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Section toggles
  const [openSections, setOpenSections] = useState({
    providers: true,
    lanes: false,
    quotes: false,
    savings: false,
    delivery: false
  });

  // Data state
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [providerTrends, setProviderTrends] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [selectedLane, setSelectedLane] = useState(null);
  const [lanePricing, setLanePricing] = useState(null);
  const [quoteMetrics, setQuoteMetrics] = useState(null);
  const [quoteTurnaround, setQuoteTurnaround] = useState(null);
  const [savingsDetail, setSavingsDetail] = useState([]);
  const [estimateVsActual, setEstimateVsActual] = useState([]);
  const [deliveryPerf, setDeliveryPerf] = useState(null);
  const [loading, setLoading] = useState({});
  const [recalculating, setRecalculating] = useState(false);

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Load providers list on mount
  useEffect(() => {
    providerAPI.getAll({ status: 'ACTIVE' }).then(res => {
      setProviders(res.data || []);
    }).catch(() => {});
  }, []);

  // Load provider scorecard
  const loadScorecard = useCallback(async (providerId) => {
    if (!providerId) return;
    setSelectedProvider(providerId);
    setLoading(l => ({ ...l, scorecard: true }));
    try {
      const [scorecardRes, trendsRes] = await Promise.all([
        analyticsAPI.getProviderScorecard(providerId),
        analyticsAPI.getProviderTrends(providerId)
      ]);
      setScorecard(scorecardRes.data);
      setProviderTrends(trendsRes.data || []);
    } catch (e) {
      console.error('Failed to load scorecard', e);
    }
    setLoading(l => ({ ...l, scorecard: false }));
  }, []);

  // Load lanes
  const loadLanes = useCallback(async () => {
    setLoading(l => ({ ...l, lanes: true }));
    try {
      const res = await analyticsAPI.getLanes();
      setLanes(res.data || []);
    } catch (e) {
      console.error('Failed to load lanes', e);
    }
    setLoading(l => ({ ...l, lanes: false }));
  }, []);

  // Load lane pricing
  const loadLanePricing = useCallback(async (origin, destination) => {
    setSelectedLane(`${origin}-${destination}`);
    setLoading(l => ({ ...l, lanePricing: true }));
    try {
      const res = await analyticsAPI.getLanePricing(origin, destination);
      setLanePricing(res.data);
    } catch (e) {
      console.error('Failed to load lane pricing', e);
    }
    setLoading(l => ({ ...l, lanePricing: false }));
  }, []);

  // Load quote metrics
  const loadQuoteMetrics = useCallback(async () => {
    setLoading(l => ({ ...l, quotes: true }));
    try {
      const [metricsRes, turnaroundRes] = await Promise.all([
        analyticsAPI.getQuoteMetrics(dateFrom, dateTo),
        analyticsAPI.getQuoteTurnaround(dateFrom, dateTo)
      ]);
      setQuoteMetrics(metricsRes.data);
      setQuoteTurnaround(turnaroundRes.data);
    } catch (e) {
      console.error('Failed to load quote metrics', e);
    }
    setLoading(l => ({ ...l, quotes: false }));
  }, [dateFrom, dateTo]);

  // Load savings
  const loadSavings = useCallback(async () => {
    setLoading(l => ({ ...l, savings: true }));
    try {
      const [savingsRes, evaRes] = await Promise.all([
        analyticsAPI.getSavingsDetail(dateFrom, dateTo),
        analyticsAPI.getEstimateVsActual(dateFrom, dateTo)
      ]);
      setSavingsDetail(savingsRes.data || []);
      setEstimateVsActual(evaRes.data || []);
    } catch (e) {
      console.error('Failed to load savings', e);
    }
    setLoading(l => ({ ...l, savings: false }));
  }, [dateFrom, dateTo]);

  // Load delivery performance
  const loadDelivery = useCallback(async () => {
    setLoading(l => ({ ...l, delivery: true }));
    try {
      const res = await analyticsAPI.getDeliveryPerformance(dateFrom, dateTo);
      setDeliveryPerf(res.data);
    } catch (e) {
      console.error('Failed to load delivery performance', e);
    }
    setLoading(l => ({ ...l, delivery: false }));
  }, [dateFrom, dateTo]);

  // Auto-load when section opens
  useEffect(() => {
    if (openSections.lanes && lanes.length === 0) loadLanes();
  }, [openSections.lanes, lanes.length, loadLanes]);

  useEffect(() => {
    if (openSections.quotes && !quoteMetrics) loadQuoteMetrics();
  }, [openSections.quotes, quoteMetrics, loadQuoteMetrics]);

  useEffect(() => {
    if (openSections.savings && savingsDetail.length === 0) loadSavings();
  }, [openSections.savings, savingsDetail.length, loadSavings]);

  useEffect(() => {
    if (openSections.delivery && !deliveryPerf) loadDelivery();
  }, [openSections.delivery, deliveryPerf, loadDelivery]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await analyticsAPI.recalculateScores();
      alert(`Recalculated ${res.data?.updated || 0} provider scores.`);
    } catch (e) {
      alert('Failed to recalculate scores.');
    }
    setRecalculating(false);
  };

  const handleExport = async (reportType) => {
    try {
      const response = await analyticsAPI.exportCsv(reportType, dateFrom, dateTo);
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}-${dateTo}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to export CSV.');
    }
  };

  const trendArrow = (trend) => {
    if (trend === 'UP') return '\u2191';
    if (trend === 'DOWN') return '\u2193';
    return '\u2192';
  };

  return (
    <div>
      <div className="page-header">
        <h1>Analytics</h1>
        <div className="header-actions">
          <label className="text-small">From:</label>
          <input type="date" className="filter-input" style={{ flex: 'none', width: 160 }}
            value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <label className="text-small">To:</label>
          <input type="date" className="filter-input" style={{ flex: 'none', width: 160 }}
            value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
      </div>

      {/* 1. Provider Scorecards */}
      <div className="analytics-section">
        <button className="section-toggle" onClick={() => toggleSection('providers')}>
          {openSections.providers ? '\u25BC' : '\u25B6'} Provider Scorecards
        </button>
        {openSections.providers && (
          <div className="dashboard-card">
            <div className="section-header-actions">
              <select className="filter-select" value={selectedProvider || ''}
                onChange={e => loadScorecard(e.target.value)}>
                <option value="">Select a provider...</option>
                {providers.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <button className="btn btn-sm btn-secondary"
                onClick={() => handleExport('provider-performance')}>Export CSV</button>
            </div>

            {loading.scorecard && <p className="loading">Loading scorecard...</p>}

            {scorecard && !loading.scorecard && (
              <div className="scorecard-grid">
                <div className="stat-grid scorecard-stats">
                  <div className="stat-item">
                    <span className="stat-value">{scorecard.provider.reliabilityScore ? (scorecard.provider.reliabilityScore * 100).toFixed(0) : 0}%</span>
                    <span className="stat-label">Reliability</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{scorecard.responseRate}%</span>
                    <span className="stat-label">Response Rate</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{scorecard.quoteStats.total}</span>
                    <span className="stat-label">Total Quotes</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">${scorecard.quoteStats.avgCost.toFixed(0)}</span>
                    <span className="stat-label">Avg Cost</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{scorecard.bookingStats.completed}</span>
                    <span className="stat-label">Bookings</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">
                      {scorecard.deliveryStats.total > 0
                        ? (scorecard.deliveryStats.onTime / scorecard.deliveryStats.total * 100).toFixed(0)
                        : 'N/A'}%
                    </span>
                    <span className="stat-label">On-Time Delivery</span>
                  </div>
                </div>

                {providerTrends.length > 0 && (
                  <>
                    <h3 style={{ marginTop: '1rem' }}>Monthly Trends</h3>
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Quotes</th>
                          <th>Avg Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {providerTrends.map((t, i) => (
                          <tr key={i}>
                            <td>{new Date(t.month).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}</td>
                            <td>{t.quoteCount}</td>
                            <td>${t.avgCost.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2. Lane Pricing */}
      <div className="analytics-section">
        <button className="section-toggle" onClick={() => toggleSection('lanes')}>
          {openSections.lanes ? '\u25BC' : '\u25B6'} Lane Pricing
        </button>
        {openSections.lanes && (
          <div className="dashboard-card">
            {loading.lanes && <p className="loading">Loading lanes...</p>}

            {lanes.length > 0 && (
              <div>
                <select className="filter-select" value={selectedLane || ''}
                  onChange={e => {
                    const [o, d] = e.target.value.split('-');
                    if (o && d) loadLanePricing(o, d);
                  }}>
                  <option value="">Select a lane...</option>
                  {lanes.map((l, i) => (
                    <option key={i} value={`${l.originPort}-${l.destinationPort}`}>
                      {l.originPort} - {l.destinationPort} ({l.requestCount} requests)
                    </option>
                  ))}
                </select>

                {loading.lanePricing && <p className="loading">Loading pricing...</p>}

                {lanePricing && !loading.lanePricing && (
                  <div style={{ marginTop: '1rem' }}>
                    <p className="text-small">
                      Trend: <span className={`trend-indicator trend-${lanePricing.trend.toLowerCase()}`}>
                        {trendArrow(lanePricing.trend)} {lanePricing.trend}
                      </span>
                    </p>
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Avg Cost</th>
                          <th>Min</th>
                          <th>Max</th>
                          <th>Quotes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lanePricing.months.map((m, i) => (
                          <tr key={i}>
                            <td>{new Date(m.month).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}</td>
                            <td>${m.avgCost.toFixed(2)}</td>
                            <td>${m.minCost.toFixed(2)}</td>
                            <td>${m.maxCost.toFixed(2)}</td>
                            <td>{m.quoteCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {!loading.lanes && lanes.length === 0 && (
              <p className="text-muted">No lane data available.</p>
            )}
          </div>
        )}
      </div>

      {/* 3. Quote Metrics */}
      <div className="analytics-section">
        <button className="section-toggle" onClick={() => toggleSection('quotes')}>
          {openSections.quotes ? '\u25BC' : '\u25B6'} Quote Metrics
        </button>
        {openSections.quotes && (
          <div className="dashboard-card">
            <div className="section-header-actions">
              <button className="btn btn-sm btn-primary" onClick={loadQuoteMetrics}>Refresh</button>
            </div>

            {loading.quotes && <p className="loading">Loading quote metrics...</p>}

            {quoteMetrics && !loading.quotes && (
              <div>
                <div className="stat-grid scorecard-stats">
                  <div className="stat-item">
                    <span className="stat-value">{quoteMetrics.totalRequests}</span>
                    <span className="stat-label">Total Requests</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{quoteMetrics.responseRate}%</span>
                    <span className="stat-label">Response Rate</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{quoteMetrics.avgFirstQuoteHours.toFixed(1)}h</span>
                    <span className="stat-label">First Quote Latency</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{quoteMetrics.coverageRate}%</span>
                    <span className="stat-label">Coverage Rate</span>
                  </div>
                </div>

                {quoteTurnaround && (
                  <div className="stat-grid scorecard-stats">
                    <div className="stat-item">
                      <span className="stat-value">{quoteTurnaround.avgTurnaroundHours}h</span>
                      <span className="stat-label">Avg Turnaround</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{quoteTurnaround.autoSelectionRate}%</span>
                      <span className="stat-label">Auto-Selection Rate</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{quoteTurnaround.recommendationAgreementRate}%</span>
                      <span className="stat-label">Recommendation Agreement</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{quoteTurnaround.totalSelections}</span>
                      <span className="stat-label">Total Selections</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 4. Cost Savings */}
      <div className="analytics-section">
        <button className="section-toggle" onClick={() => toggleSection('savings')}>
          {openSections.savings ? '\u25BC' : '\u25B6'} Cost Savings
        </button>
        {openSections.savings && (
          <div className="dashboard-card">
            <div className="section-header-actions">
              <button className="btn btn-sm btn-primary" onClick={loadSavings}>Refresh</button>
              <button className="btn btn-sm btn-secondary"
                onClick={() => handleExport('cost-savings')}>Export CSV</button>
            </div>

            {loading.savings && <p className="loading">Loading savings...</p>}

            {!loading.savings && (
              <div>
                {savingsDetail.length > 0 && (
                  <>
                    <h3>Per-Shipment Savings</h3>
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Lane</th>
                          <th>Provider</th>
                          <th>Savings</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {savingsDetail.map((s, i) => (
                          <tr key={i}>
                            <td>{s.orderId || '-'}</td>
                            <td>{s.lane || '-'}</td>
                            <td>{s.provider || '-'}</td>
                            <td className="savings-value">${s.costSavings.toFixed(2)}</td>
                            <td>{s.comparedAt ? new Date(s.comparedAt).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {estimateVsActual.length > 0 && (
                  <>
                    <h3 style={{ marginTop: '1.5rem' }}>Estimate vs Actual</h3>
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Lane</th>
                          <th>Estimated</th>
                          <th>Actual</th>
                          <th>Variance</th>
                          <th>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estimateVsActual.map((s, i) => (
                          <tr key={i}>
                            <td>{s.orderId || '-'}</td>
                            <td>{s.lane}</td>
                            <td>${s.estimated.toFixed(2)}</td>
                            <td>${s.actual.toFixed(2)}</td>
                            <td style={{ color: s.variance > 0 ? 'var(--danger-color)' : 'var(--success-color)' }}>
                              ${s.variance.toFixed(2)}
                            </td>
                            <td>{s.variancePercent}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {savingsDetail.length === 0 && estimateVsActual.length === 0 && (
                  <p className="text-muted">No cost data available for this period.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 5. Estimate Accuracy */}
      <div className="analytics-section">
        <div className="dashboard-card">
          <EstimateAccuracy />
        </div>
      </div>

      {/* 6. Delivery Performance */}
      <div className="analytics-section">
        <button className="section-toggle" onClick={() => toggleSection('delivery')}>
          {openSections.delivery ? '\u25BC' : '\u25B6'} Delivery Performance
        </button>
        {openSections.delivery && (
          <div className="dashboard-card">
            <div className="section-header-actions">
              <button className="btn btn-sm btn-primary" onClick={loadDelivery}>Refresh</button>
              <button className="btn btn-sm btn-secondary" onClick={handleRecalculate}
                disabled={recalculating}>
                {recalculating ? 'Recalculating...' : 'Recalculate Scores'}
              </button>
            </div>

            {loading.delivery && <p className="loading">Loading delivery data...</p>}

            {deliveryPerf && !loading.delivery && (
              <div>
                <div className="stat-grid scorecard-stats">
                  <div className="stat-item">
                    <span className="stat-value">{deliveryPerf.total}</span>
                    <span className="stat-label">Total Deliveries</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{deliveryPerf.onTimeRate}%</span>
                    <span className="stat-label">On-Time Rate</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{deliveryPerf.avgDelayHours}h</span>
                    <span className="stat-label">Avg Delay</span>
                  </div>
                </div>

                {deliveryPerf.providerBreakdown.length > 0 && (
                  <>
                    <h3>By Provider</h3>
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Provider</th>
                          <th>Deliveries</th>
                          <th>On-Time Rate</th>
                          <th>Avg Delay (h)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveryPerf.providerBreakdown.map((p, i) => (
                          <tr key={i}>
                            <td>{p.providerName}</td>
                            <td>{p.total}</td>
                            <td>{p.onTimeRate}%</td>
                            <td>{p.avgDelay}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}

            {!loading.delivery && !deliveryPerf && (
              <p className="text-muted">No delivery data available.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
